<template>
  <p class="scrub-input">
    <!-- D-240: `min`/`max`/`step` were implicit here too, and declaring them is the
         same measured no-op as in the v3/v4 siblings (0-100 step 1).

         🔴 BUT THIS ONE IS NOT JUST THE v4 FIX RE-TYPED, AND THE DIFFERENCE IS A
         DEFECT THE PLAN DID NOT HAVE. v3 and v4 render NO range on a live stream
         (`v-if="!isLiveStream"`), so the plan's live-stream carve-out — "there is
         nothing to label" — is true of them. v1 renders the range REGARDLESS and
         defends itself inside the handler, snapping the value back to 0. So v1 ships a
         focusable, arrow-operable slider that silently refuses every input: a control
         that lies, and the one shape a keyboard user cannot diagnose.

         `aria-disabled`, not `disabled`, for A3's reason (D-234): the attribute blurs a
         focused control, and the guard clause below is already the real enforcement.
         This announces the state the handler has always implemented. -->
    <input type="range"
           min="0"
           max="100"
           step="1"
           aria-label="Audio position"
           v-bind:aria-disabled="playingLiveStream ? 'true' : null"
           v-bind:aria-valuetext="valueText"
           v-bind:value="position"
           v-on:input="changed">
  </p>
</template>

<script>
import formatDuration from '../utils/formatDuration';
import scrubberValueText from '../shared-components/scrubberValueText';

export default {
  mixins: [scrubberValueText],
  // Vue3: declare the emitted event (Scrubber emits `seeking` to media_player).
  emits: ['seeking'],
  // `timeLeft` is new in A5 and is the ONLY time datum this component needs, because
  // it is the only one v1 displays — see `spokenPosition`. `media_player` already held
  // it as a prop and simply never passed it down.
  props: ['position', 'playingLiveStream', 'timeLeft'],
  computed: {
    /**
     * D-240. Consumed by the `scrubberValueText` mixin, which rate-limits publishing
     * it into `aria-valuetext`.
     *
     * ⚠️ REMAINING ONLY, and that is not an oversight copied wrong from v4. v1's
     * visible readout is a single `-{{ formattedTimeLeft }}`; there is no elapsed
     * label anywhere in `media_player`. Announcing an elapsed time this version never
     * shows would put the screen-reader user and the sighted user on different
     * numbers, which is the defect A7 fixes in Pagination.
     */
    spokenPosition() {
      // Nothing honest to say while the range is inoperable — and v1's own readout
      // switches to "LIVE" here rather than showing a time, so a spoken duration
      // would contradict it.
      if (this.playingLiveStream) return null;

      const remaining = formatDuration(this.timeLeft);
      if (remaining === null) return null;

      return `${remaining} remaining`;
    },
  },
  methods: {
    changed(ev) {
      if (this.playingLiveStream) {
        // prevent user from seeking during livestream
        ev.currentTarget.value = 0; // eslint-disable-line no-param-reassign
        return;
      }
      // Arms an immediate announcement for the position that ARRIVES, not the one
      // being left — the mixin's 🔴 note has the measurement.
      this.markUserSeek();
      this.$emit('seeking', +ev.currentTarget.value);
    },
  },
};
</script>

<style lang="less" scoped>
  @track-color: fade(#999999, 50%);
  @thumb-color: #ffffff;

  @thumb-radius: 9px;
  @thumb-height: 16px;
  @thumb-width: 16px;
  @thumb-shadow-size: 1px;
  @thumb-shadow-blur: 2px;
  @thumb-shadow-color: fade(#000000,.5);
  @thumb-border-width: 1px;
  @thumb-border-color: #666666;

  @track-width: 100%;
  @track-height: 6px;
  @track-border-width: 1px;
  @track-border-color: #666666;

  @track-radius: 3px;
  @contrast: 5%;

  * {box-sizing: border-box; margin: 0; padding: 0;}


  /* Samples:
  http://danielstern.ca/range.css/#/
  https://css-tricks.com/styling-cross-browser-compatible-range-inputs-css/
  */

  .shadow(@shadow-size,@shadow-blur,@shadow-color) {
    box-shadow: @shadow-size @shadow-size @shadow-blur @shadow-color, 0px 0px @shadow-size lighten(@shadow-color,5%);
  }

  .track() {
    width: @track-width;
    height: @track-height;
    cursor: pointer;
    animate: 0.2s;
  }

  .thumb() {
    .shadow(@thumb-shadow-size,@thumb-shadow-blur,@thumb-shadow-color);
    border: @thumb-border-width solid @thumb-border-color;
    height: @thumb-height;
    width: @thumb-width;
    border-radius: @thumb-radius;
    background: @thumb-color;
    cursor: pointer;
  }

  input[type=range] {
    background: transparent;
    -webkit-appearance: none;
    margin: 0 0;
    padding: 0;
    width: @track-width;

    &:focus {
      outline: none;
    }

    &::-webkit-slider-runnable-track {
      .track();
      background: @track-color;
      border-radius: @track-radius;
      border: @track-border-width solid @track-border-color;
    }

    &::-webkit-slider-thumb {
      .thumb();
      -webkit-appearance: none;
      margin-top: ((-@track-border-width * 2 + @track-height) / 2) - (@thumb-height / 2);
    }

    &:focus::-webkit-slider-runnable-track {
      background: lighten(@track-color, @contrast);
    }

    &::-moz-range-track {
      .track();
      background: @track-color;
      border-radius: @track-radius;
      border: @track-border-width solid @track-border-color;
    }
    &::-moz-range-thumb {
      .thumb();
    }

    &::-ms-track {
      .track();
      background: transparent;
      border-color: transparent;
      border-width: @thumb-width 0;
      color: transparent;
    }

    &::-ms-fill-lower {
      background: darken(@track-color, @contrast);
      border: @track-border-width solid @track-border-color;
      border-radius: @track-radius*2;
    }

    &::-ms-fill-upper {
      background: @track-color;
      border: @track-border-width solid @track-border-color;
      border-radius: @track-radius*2;
    }

    &::-ms-thumb {
      .thumb();
    }

    &:focus::-ms-fill-lower {
      background: @track-color;
    }

    &:focus::-ms-fill-upper {
      background: lighten(@track-color, @contrast);
    }
  }

</style>
